import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type { Me, SignInMethods } from '../api/types'
import { radius } from '../components/primitives/shape'

/** Where the browser goes to hand itself to GitHub and come back signed in. */
const GITHUB_SIGN_IN = '/v1/auth/github'

/** The same round trip, at the shared Enum Labs Google client. */
const GOOGLE_SIGN_IN = '/v1/auth/google'

/**
 * Why a GitHub sign-in came back without signing anyone in.
 *
 * The server sends a code and the copy lives here, rather than the server
 * sending a sentence. That is not ceremony: the code arrives in a query string,
 * and anyone can put anything in one. An unrecognised code falls through to
 * the generic line, so nothing a stranger types can be rendered.
 */
const AUTH_ERRORS: Record<string, string> = {
  expired: 'That sign-in attempt had expired. Try again.',
  no_email:
    'GitHub has no verified primary email on that account. Add one on GitHub, or sign up with an email address and password.',
  // Deliberately not "try again": this one is the server's setup, and retrying
  // will fail exactly the same way until somebody grants the permission.
  not_permitted:
    'GitHub would not share your email address with stagepost. That is this end, not yours — sign up with an email address and password, and tell whoever runs this server.',
  unavailable: 'GitHub did not answer. Try again in a moment.',
  // Google's codes are separate from GitHub's on purpose. The copy above names
  // GitHub in every line, and sharing the codes would answer a failed Google
  // sign-in by telling somebody to go and fix their GitHub account.
  google_expired: 'That sign-in attempt had expired. Try again.',
  google_no_email:
    'Google has not verified the email address on that account, so we cannot match it to an account here. Sign up with an email address and password instead.',
  google_unavailable: 'Google did not answer. Try again in a moment.',
  // The same sentence as the server's 403 on POST /signup.
  signup_closed: 'Sign-up on this server is closed. Ask its owner for an invitation.',
}

const GENERIC_AUTH_ERROR = 'That did not work. Try again.'

/**
 * What the panel calls signing up, on a server that takes new accounts.
 *
 * A deployment can say it in its own words: stagepost.io calls it early
 * access. The invitation-only lines are not part of it, because they describe
 * the server rather than ask anyone in.
 */
export interface SignUpWording {
  title: string
  switchToSignup: string
  submit: string
}

const OPEN_SIGN_UP: SignUpWording = {
  title: 'Create an account',
  switchToSignup: 'New here? Create an account',
  submit: 'Sign up free',
}

/**
 * Read `?auth_error=` once, then take it out of the address bar.
 *
 * Left in place, a refresh re-shows an error about an attempt that is long
 * over, and the URL follows the person around if they bookmark the page.
 */
function consumeAuthError(): string | null {
  if (typeof window === 'undefined') return null
  const found = new URLSearchParams(window.location.search).get('auth_error')
  if (!found) return null

  const url = new URL(window.location.href)
  url.searchParams.delete('auth_error')
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`)

  return AUTH_ERRORS[found] ?? GENERIC_AUTH_ERROR
}

/**
 * Where the page stops arguing and asks.
 *
 * The design file points every call to action at `app.stagepost.io/signup`,
 * which is the right shape for a marketing site that is a separate deployment.
 * It is not ours: the marketing page and the app are one SPA on one origin, and
 * a logged-out visitor is already standing in the app. So the closing call to
 * action *is* the form, and every button above it scrolls here.
 */
export function AuthPanel({
  mode,
  onModeChange,
  onSignedIn,
  wording = OPEN_SIGN_UP,
}: {
  mode: 'signup' | 'login'
  onModeChange: (mode: 'signup' | 'login') => void
  onSignedIn: (me: Me) => void
  wording?: SignUpWording
}) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  // Read on the first render rather than in an effect: the redirect back from
  // GitHub is the page's first paint, and an effect would flash the form with
  // no explanation on it before the reason appeared.
  const [error, setError] = useState<string | null>(consumeAuthError)
  const [busy, setBusy] = useState(false)
  const [methods, setMethods] = useState<SignInMethods>({
    password: true,
    github: false,
    google: false,
    password_reset: false,
    signup: true,
  })
  // The forgot-password view is a mode of this panel rather than a screen of
  // its own: it is the same box, the same field, and one step away from the
  // form they were just looking at.
  const [forgetting, setForgetting] = useState(false)
  const [sent, setSent] = useState(false)

  // A deployment without a GitHub OAuth app is supported, and its 503 is not
  // something to show a visitor — so ask before offering the button. A failed
  // ask leaves it hidden, which is the same outcome as an unconfigured server.
  useEffect(() => {
    let live = true
    api
      .signInMethods()
      .then((found) => {
        if (live) setMethods(found)
      })
      .catch(() => {})
    return () => {
      live = false
    }
  }, [])

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const me = mode === 'signup' ? await api.signup({ email, password }) : await api.login({ email, password })
      onSignedIn(me)
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'that did not work')
    } finally {
      setBusy(false)
    }
  }

  async function requestLink(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    try {
      await api.forgotPassword({ email })
    } catch {
      // Deliberately swallowed. The server answers 204 for an unknown address
      // so this cannot be used to find out who has an account, and a visible
      // failure here would give back exactly what that 204 withholds.
    } finally {
      setBusy(false)
      setSent(true)
    }
  }

  const field = {
    fontSize: 13.5,
    color: 'var(--text)',
    background: 'var(--panel-strong)',
    border: '1px solid var(--border-strong)',
    borderRadius: radius.sm,
    padding: '11px 14px',
  } as const

  const panel = {
    margin: '0 auto',
    maxWidth: 420,
    textAlign: 'left',
    background: 'var(--panel)',
    border: '1px solid var(--border-strong)',
    borderRadius: radius.xl,
    padding: 'clamp(18px, 5vw, 26px)',
  } as const

  /**
   * The quiet links under the form — switching mode, asking for a link, going
   * back. Drawn as text, so they had no height beyond their own line: 15px is
   * a third of a fingertip, and they sit close enough together that one tap
   * could reach either. Real padding rather than an expanded hit area, which
   * on three stacked controls would overlap and hand the tap to a neighbour.
   */
  const textButton = {
    display: 'flex',
    alignItems: 'center',
    minHeight: 40,
    padding: '0 2px',
    fontSize: 12.5,
    cursor: 'pointer',
    textAlign: 'left',
  } as const

  /**
   * A provider button. `first` carries the gap off the paragraph above it; the
   * ones after it sit closer, so two providers read as one group of ways in
   * rather than two unrelated buttons.
   */
  const socialButton = (first: boolean) =>
    ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 10,
      marginTop: first ? 18 : 10,
      padding: '12px 18px',
      borderRadius: radius.action,
      background: 'var(--panel-strong)',
      border: '1px solid var(--border-strong)',
      color: 'var(--text)',
      fontSize: 14,
      fontWeight: 700,
    }) as const

  const submitButton = (disabled: boolean) =>
    ({
      padding: '13px 18px',
      borderRadius: radius.action,
      background: 'var(--accent)',
      color: 'var(--ground)',
      fontSize: 14.5,
      fontWeight: 700,
      cursor: 'pointer',
      opacity: disabled ? 0.6 : 1,
    }) as const

  // Whether there is anything above the divider at all. A deployment can have
  // neither provider configured, and the divider then separates the form from
  // nothing.
  const social = methods.github || methods.google
  const provider =
    methods.github && methods.google ? 'GitHub and Google' : methods.github ? 'GitHub' : 'Google'

  if (forgetting) {
    return (
      <div style={panel}>
        <h3 style={{ margin: '0 0 4px', fontSize: 17, fontWeight: 800, letterSpacing: '-.02em' }}>
          Reset your password
        </h3>
        {sent ? (
          <p role="status" style={{ margin: '10px 0 0', fontSize: 13, lineHeight: 1.6, color: 'var(--text-dim)' }}>
            If that address has an account, we have sent a link to it. The link is good for one hour.
          </p>
        ) : (
          <>
            <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, color: 'var(--text-dim)' }}>
              Tell us the address you signed up with and we will send you a link.
            </p>
            <form onSubmit={requestLink} style={{ display: 'grid', gap: 10, marginTop: 18 }}>
              <input
                type="email"
                required
                value={email}
                aria-label="Email"
                placeholder="name@company.com"
                onChange={(event) => setEmail(event.target.value)}
                style={field}
              />
              <button type="submit" disabled={busy} style={submitButton(busy)}>
                Send me a link
              </button>
            </form>
          </>
        )}
        <button
          type="button"
          onClick={() => {
            setForgetting(false)
            setSent(false)
          }}
          style={{ ...textButton, marginTop: 8, color: 'var(--accent)' }}
        >
          Back to sign in
        </button>
      </div>
    )
  }

  return (
    <div style={panel}>
      <h3 style={{ margin: '0 0 4px', fontSize: 17, fontWeight: 800, letterSpacing: '-.02em' }}>
        {mode === 'signup' ? wording.title : 'Sign in'}
      </h3>
      <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, color: 'var(--text-dim)' }}>
        Once you are in, install the stagepost command and run stagepost login.
      </p>

      {social && (
        <>
          {methods.github && (
            <a href={GITHUB_SIGN_IN} style={socialButton(true)}>
              <GithubMark />
              {mode === 'signup' ? 'Sign up with GitHub' : 'Log in with GitHub'}
            </a>
          )}
          {methods.google && (
            <a href={GOOGLE_SIGN_IN} style={socialButton(!methods.github)}>
              <GoogleMark />
              {mode === 'signup' ? 'Sign up with Google' : 'Log in with Google'}
            </a>
          )}
          <Divider />
        </>
      )}

      <form onSubmit={submit} style={{ display: 'grid', gap: 10, marginTop: social ? 0 : 18 }}>
        <input
          type="email"
          required
          value={email}
          aria-label="Email"
          placeholder="name@company.com"
          onChange={(event) => setEmail(event.target.value)}
          style={field}
        />
        <input
          type="password"
          required
          value={password}
          aria-label="Password"
          placeholder="a password you will remember"
          onChange={(event) => setPassword(event.target.value)}
          style={field}
        />
        {error && (
          <p role="alert" style={{ margin: 0, fontSize: 12.5, color: 'var(--deletions)' }}>
            {error}
          </p>
        )}
        <button type="submit" disabled={busy} style={submitButton(busy)}>
          {mode === 'signup' ? wording.submit : 'Log in'}
        </button>
      </form>

      {mode === 'login' && methods.password_reset && (
        <button
          type="button"
          onClick={() => {
            setForgetting(true)
            setError(null)
          }}
          style={{ ...textButton, marginTop: 6, color: 'var(--text-dim)' }}
        >
          Forgot your password?
        </button>
      )}

      {!methods.signup && (
        <p style={{ margin: '12px 0 0', fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-dim)' }}>
          Accounts on this server are by invitation.
        </p>
      )}

      {/* Kept on an invitation-only server: without mail, signing up with the
          invited address is the only way an invited person gets in. */}
      <button
        type="button"
        onClick={() => {
          onModeChange(mode === 'signup' ? 'login' : 'signup')
          setError(null)
        }}
        style={{ ...textButton, marginTop: 8, color: 'var(--accent)' }}
      >
        {mode === 'signup'
          ? 'Already have an account? Log in'
          : methods.signup
            ? wording.switchToSignup
            : 'Invited? Create your account with the address you were invited at.'}
      </button>

      {social && (
        <p style={{ margin: '12px 0 0', fontSize: 11.5, lineHeight: 1.55, color: 'var(--text-dim)' }}>
          {provider} sign-in reads your name and verified email, nothing else. It lands you in the same account
          as the matching email address.
        </p>
      )}
    </div>
  )
}

/** The Octocat, at the one size this page uses it. */
function GithubMark() {
  return (
    <svg width="17" height="17" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.01 8.01 0 0 0 16 8c0-4.42-3.58-8-8-8Z" />
    </svg>
  )
}

/**
 * Google's G, in its four brand colours at the one size this page uses it.
 *
 * Not `currentColor` like the Octocat: Google's terms for the sign-in button
 * require the mark in its own colours, and it is the same artwork on light and
 * dark, so there is nothing to invert.
 */
function GoogleMark() {
  return (
    <svg width="17" height="17" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"
      />
      <path
        fill="#34A853"
        d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"
      />
      <path
        fill="#FBBC05"
        d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"
      />
      <path
        fill="#EA4335"
        d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"
      />
    </svg>
  )
}

/** "or" on a hairline. Keeps the two ways in from reading as a stack of buttons. */
function Divider() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0 14px' }}>
      <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
      <span
        style={{
          font: '400 10.5px var(--font-machine)',
          letterSpacing: '.14em',
          textTransform: 'uppercase',
          color: 'var(--text-dim)',
        }}
      >
        or
      </span>
      <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
    </div>
  )
}
